import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  MoreVert,
  Delete,
  AccessTime,
  AttachMoney,
  Person,
  Business,
  Category,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import ClassFormDialog from '../components/ClassFormDialog';

interface ClassDetail {
  _id: string;
  className: string;
  description: string;
  image?: string;
  duration: number;
  price: number;
  payAtStudio: boolean;
  instructor?: {
    _id: string;
    name: string;
    image?: string;
    description?: string;
  };
  classType?: {
    _id: string;
    title: string;
  };
  studio?: {
    _id: string;
    studioName: string;
    address?: string;
    email?: string;
    studioPermissions?: {
      enableSessionTypes?: boolean;
    };
  };
  createdAt: string;
  updatedAt: string;
}

const ClassDetailPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const [classDetail, setClassDetail] = useState<ClassDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchClassDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getClassById(classId!);
      setClassDetail(response.class);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch class details');
    } finally {
      setIsLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (classId) {
      fetchClassDetails();
    }
  }, [classId, fetchClassDetails]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleMenuClose();
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      // Pinned to the class's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.deleteClass(classId!, studioIdOf(classDetail?.studio));
      navigate('/classes');
    } catch (err: any) {
      setError(err.message || 'Failed to delete class');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleStudioClick = () => {
    if (classDetail?.studio?._id) {
      navigate(`/studios/${classDetail.studio._id}`);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !classDetail) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Class not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            {classDetail.image && (
              <Avatar
                src={classDetail.image}
                variant="rounded"
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                }}
              />
            )}
            <Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="h4" fontWeight={700}>
                  {classDetail.className}
                </Typography>
                {classDetail.classType && (
                  <Chip 
                    label={classDetail.classType.title} 
                    size="small" 
                    sx={{ backgroundColor: colors.backgroundTertiary, border: `1px solid ${colors.divider}` }} 
                  />
                )}
                {classDetail.payAtStudio && (
                  <Chip
                    label="Pay at Studio"
                    size="small"
                    sx={{ backgroundColor: `${colors.warning}15`, color: colors.warning }}
                  />
                )}
              </Box>
              {classDetail.studio && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mt: 0.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={handleStudioClick}
                >
                  {classDetail.studio.studioName}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button variant="outlined" size="small" startIcon={<Edit />} onClick={handleEditClick}>
              Edit
            </Button>
            <IconButton size="small" onClick={handleMenuClick}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleEditClick}>
                <Edit fontSize="small" sx={{ mr: 1 }} /> Edit Class
              </MenuItem>
              <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                <Delete fontSize="small" sx={{ mr: 1 }} /> Delete Class
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Left Column - Details */}
        <Box>
          {/* Image */}
          {classDetail.image && (
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  component="img"
                  src={classDetail.image}
                  alt={classDetail.className}
                  sx={{
                    width: '100%',
                    height: { xs: 200, sm: 300 },
                    objectFit: 'cover',
                    borderRadius: 2,
                  }}
                />
              </CardContent>
            </Card>
          )}

          {/* Description */}
          {classDetail.description && (
            <Card sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  About This Class
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  {classDetail.description}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Right Column - Info */}
        <Box>
          <Card sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Class Details
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {classDetail.duration} minutes
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Price
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      ₹{classDetail.price}
                    </Typography>
                  </Box>
                </Box>

                {classDetail.instructor && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Instructor
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {classDetail.instructor.name}
                      </Typography>
                      {classDetail.instructor.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {classDetail.instructor.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {classDetail.studio && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Studio
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight={600}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={handleStudioClick}
                      >
                        {classDetail.studio.studioName}
                      </Typography>
                      {classDetail.studio.address && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {classDetail.studio.address}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {classDetail.classType && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Category fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Class Type
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {classDetail.classType.title}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Payment Info */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Payment Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {classDetail.payAtStudio 
                  ? 'Payment to be made at the studio location'
                  : 'Payment can be made online'}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Edit Dialog */}
      {classDetail.studio?._id && (
        <ClassFormDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            fetchClassDetails();
          }}
          onSuccess={() => {
            fetchClassDetails();
            setEditDialogOpen(false);
          }}
          classItem={classDetail}
          studioId={classDetail.studio._id}
          enableSessionTypes={classDetail.studio.studioPermissions?.enableSessionTypes || false}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: colors.error, fontWeight: 700 }}>
          ⚠️ Delete Class - Critical Action
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              This action will permanently delete:
            </Typography>
            <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 0 }}>
              <li>All sessions associated with this class</li>
              <li>All bookings for those sessions</li>
              <li>The class definition and all its data</li>
            </Typography>
          </Alert>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            Are you absolutely sure you want to permanently delete class "
            <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
              {classDetail.className}
            </Box>
            "? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassDetailPage;

